import React, { useCallback } from 'react'

import { useDispatch } from 'react-redux'

import { Recipe } from '@library/menu-service'
import { MenuApiPreferencesResponse } from '@library/menu-service/queries/menuApiPreferences/types'

import { useCurrentCollectionId } from 'hooks/menu-collections'
import { useMenuMeta } from 'hooks/menu-metadata/useMenuMeta'
import { useStaticArray } from 'hooks/useStaticArray'
import { selectRecipeVariant, showDetailRecipe } from 'routes/Menu/actions/menuRecipeDetails'
import { CaloriesTutorial } from 'routes/Menu/components/MenuPreferences'
import { useBasket } from 'routes/Menu/domains/basket'
import { useCollectionRecipes, useSetBrowserCTAVisibility } from 'routes/Menu/domains/menu'

import { CTAToAllRecipes } from '../CTAToAllRecipes'
import { RecipeTile } from './RecipeTileWrapper'
import { useCustomisationEducation } from './useCustomisationEducation'
import { useTrackRecipeListEffect } from './useTrackRecipeListEffect'
import { useTrackSoldOutRecipesEffect } from './useTrackSoldOutRecipesEffect'

import css from './RecipeList.css'

export const RecipeList = ({
  menuPreferences,
}: {
  menuPreferences?: MenuApiPreferencesResponse
}) => {
  const currentCollectionId = useCurrentCollectionId()
  const { menuId } = useMenuMeta()

  const recipeSets = useCollectionRecipes(currentCollectionId)

  const immutableRecipes = useStaticArray(recipeSets.map(({ recipe }) => recipe))
  useTrackSoldOutRecipesEffect(immutableRecipes)
  useTrackRecipeListEffect(currentCollectionId, immutableRecipes)

  const dispatch = useDispatch()

  const { canAddRecipes, addRecipe, getQuantitiesForRecipeId, removeRecipe, reachedLimit } =
    useBasket()

  const { setBrowserCTAVisible } = useSetBrowserCTAVisibility()

  const onClick = useCallback((recipeUuid) => dispatch(showDetailRecipe(recipeUuid)), [dispatch])

  const selectVariant = useCallback(
    ({
      variant,
      variantOutOfStock,
      originalRecipeId,
      recipeReference,
    }: {
      variant: Recipe
      variantOutOfStock: boolean
      originalRecipeId: string
      recipeReference: string
    }) => {
      dispatch(
        selectRecipeVariant({
          originalRecipeId,
          variant,
          collectionId: currentCollectionId,
          variantOutOfStock,
          view: 'grid',
          close: true,
          recipeReference,
        }),
      )
    },
    [currentCollectionId, dispatch],
  )

  const { recipeIdForEducation, closeEducation } = useCustomisationEducation(recipeSets)

  return (
    <div className={css.threeTileRecipeList}>
      {recipeSets.map((value, index: number) => (
        <React.Fragment key={value.reference}>
          {index === 5 && <CaloriesTutorial />}
          <RecipeTile
            recipe={value.recipe}
            currentCollectionId={currentCollectionId}
            menuId={menuId}
            onClick={onClick}
            menuPreferences={menuPreferences}
            setBrowserCTAVisible={setBrowserCTAVisible}
            canAddRecipes={canAddRecipes}
            addRecipe={addRecipe}
            removeRecipe={removeRecipe}
            reachedLimit={reachedLimit}
            selectVariant={selectVariant}
            recipeReference={value.reference}
            originalId={value.originalId}
            index={index + 1}
            quantity={getQuantitiesForRecipeId(value.recipe.coreId)}
            showCustomisationEducation={value.recipe.id === recipeIdForEducation}
            closeCustomisationEducation={closeEducation}
          />
        </React.Fragment>
      ))}
      <CTAToAllRecipes />
    </div>
  )
}
